import { deviceConfigDeleted, deviceConfigsReload, deviceConfigsSuccess } from "actions/deviceConfigActions";
import { callApi } from "apiClient";
import { useAsync, useDeviceConfigs, useProtocols, useSession, useServiceMessageSets, useVoiceSets } from "hooks";
import { useDeviceTypes } from "hooks/useDeviceTypes/useDeviceTypes";
import { BuildingId, DeviceConfig, DeviceConfigId, DeviceTypeId } from 'shared/models';
import { Permission } from 'shared/permissions';
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Dialog } from 'ui/shared/containers';
import { toError } from 'shared/util/toError';
import { DeviceConfigForm } from "./deviceConfigForm/DeviceConfigForm";

require('./DeviceConfigDialog.css');

export type Props = {
    visible: boolean;
    buildingId: BuildingId;
    deviceTypeId: DeviceTypeId | null;
    readOnly?: boolean;
    isPI: boolean;
    deviceConfigId: DeviceConfigId | null;
    onClose(): void;

    // Used to change the selected device config of the caller. May be omitted if not needed.
    onChangeId?(deviceConfigId: DeviceConfigId | null): void;
};

export const DeviceConfigDialog = ({ visible, deviceConfigId, buildingId, deviceTypeId, readOnly = false, isPI, onClose, onChangeId }: Props) => {
    const { isMounted } = useAsync();
    const { hasPermission, user } = useSession();
    const { deviceConfigs, loading } = useDeviceConfigs(buildingId);
    const { deviceTypes } = useDeviceTypes();
    const { voiceSets } = useVoiceSets(user!.orgId);
    const { protocols } = useProtocols({ buildingId });
    const { serviceMessageSets } = useServiceMessageSets(user!.orgId);
    const [ busy, setBusy ] = useState(false);
    const [ errorKey, setErrorKey ] = useState<string | null>(null);

    const dispatch = useDispatch();

    const config = (deviceConfigId && deviceConfigs[deviceConfigId]) || null;
    const deviceType = (deviceTypes && deviceTypeId && deviceTypes[deviceTypeId]) || null;

    const handleSave = useCallback(async (config: DeviceConfig, asNew: boolean, closeDialog: boolean): Promise<DeviceConfig | null> => {
        if (!config.displayName) {
            setErrorKey(`NAME_REQUIRED`);
            return null;
        }
        if (!config.protocolId) {
            setErrorKey(`PROTOCOL_REQUIRED`);
            return null;
        }
        try {
            setErrorKey(null);
            setBusy(true);
            let result: DeviceConfig;
            if (config.id) {
                if (asNew) {
                    result = await callApi('POST', `/api/buildings/${buildingId}/device-configs/${config.id}`, config);
                }
                else {
                    result = await callApi('PUT', `/api/buildings/${buildingId}/device-configs/${config.id}`, config);
                }
            }
            else {
                result = await callApi('POST', `/api/buildings/${buildingId}/device-configs`, config);
            }
            dispatch(deviceConfigsSuccess([ result ], { buildingId }));
            if (closeDialog) {
                dispatch(deviceConfigsReload(buildingId));
                onClose();
            }
            if (config.id !== result.id && onChangeId) {
                onChangeId(result.id);
            }
            return result;
        }
        catch (e) {
            const err = toError(e);
            if (isMounted()) {
                setErrorKey(err.message);
            }
            return null;
        }
        finally {
            if (isMounted()) {
                setBusy(false);
            }
        }
    }, [ buildingId, setErrorKey, setBusy, onChangeId, dispatch, isMounted, onClose ]);

    const handleDelete = useCallback((deviceConfigId: DeviceConfigId) => {
        setErrorKey(null);
        setBusy(true);
        void (async () => {
            try {
                await callApi('DELETE', `/api/buildings/${buildingId}/device-configs/${deviceConfigId}`);
                onClose();
                onChangeId && onChangeId(null);
                dispatch(deviceConfigDeleted(deviceConfigId));
            }
            catch (e) {
                if (onChangeId) {
                    // Just log the error and continue as if the delete succeeded
                    console.error(e);
                    onClose();
                    onChangeId(null);
                    dispatch(deviceConfigDeleted(deviceConfigId));
                }
                else {
                    const err = toError(e);
                    isMounted() && setErrorKey(err.message);
                }
            }
            finally {
                isMounted() && setBusy(false);
            }
        })();
    }, [ buildingId, setErrorKey, setBusy, onClose, onChangeId, dispatch, isMounted ]);

    // Wipe out previous error when we get displayed again
    useEffect(() => {
        if (visible) {
            setErrorKey(null);
            setBusy(false);
        }
    }, [ visible, setErrorKey ]);

    if (!voiceSets || !deviceConfigs || !protocols || !serviceMessageSets || loading) {
        return null;
    }
    return (
        <Dialog
            id="device-config-dialog"
            visible={ visible }
            titleKey={ deviceConfigId ? 'DEVICE_CONFIGS.DIALOG.EDIT_TITLE' : 'DEVICE_CONFIGS.DIALOG.ADD_TITLE' }
            portalName="device-config-portal"
        >
            <DeviceConfigForm
                buildingId={ buildingId }
                readOnly={ readOnly }
                deviceType={ deviceType }
                isPI={ isPI }
                busy={ busy }
                canDelete={ hasPermission(buildingId, Permission.EditDevice) }
                errorKey={ errorKey }
                onSave={ handleSave }
                onDelete={ handleDelete }
                config={ config }
                onCancel={ onClose }
                protocols={ protocols }
                serviceMessageSets={ serviceMessageSets }
                voiceSets={ voiceSets }
            />
        </Dialog>
    );
};
